import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CheckmarkCircle12Filled,
    Circle12Regular,
    Dismiss12Regular,
    Document16Regular,
    Warning12Filled,
} from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { toast } from 'react-toastify';
import axiosInstance from '../../../apiServices/axios';
import {
    INTELLIGENCE_INSIGHTS_V2_HIGHLIGHTS_EVIDENCE,
    INTELLIGENCE_INSIGHTS_V2_OBJECT_EVIDENCE,
    INTELLIGENCE_INSIGHTS_V2_SCOPE_EVIDENCE,
} from '../../../apiServices/api_routes';
import { filterSupportedInsightsActions } from '../../../utils/intelligenceInsightsActions';
import styles from './intelligenceInsightsHighlights.module.css';
import { isIntelligenceInsightsScopeArea } from '../../../utils/intelligenceInsightsModuleViewModels';
import { useInsightsAnalysisContext, withInsightsContextQuery } from '../../../utils/intelligenceInsightsContext';

const TABS = [
    { key: 'supporting', label: 'Supporting', apiKey: 'supporting' },
    { key: 'contradictory', label: 'Contradictory', apiKey: 'contradictory' },
    { key: 'claims', label: 'Claims & Inferences', apiKey: 'claims_and_inferences' },
    { key: 'gaps', label: 'Gaps', apiKey: 'gaps' },
];

const RAG_CLASS = {
    amber: styles.ragAmber,
    green: styles.ragGreen,
    red: styles.ragRed,
    grey: styles.ragGrey,
};

const SWOT_STRENGTH_BARS = [
    { key: 'source_quality', fallback: 'quality', label: 'Source quality' },
    { key: 'corroboration', fallback: 'diversity', label: 'Corroboration' },
    { key: 'recency', fallback: 'recency', label: 'Recency' },
    { key: 'completeness', fallback: 'completeness', label: 'Completeness' },
    { key: 'relevance', fallback: 'relevance', label: 'Relevance' },
];

const HIGHLIGHT_STRENGTH_BARS = [
    { key: 'relevance', label: 'Relevance' },
    { key: 'quality', label: 'Quality' },
    { key: 'diversity', label: 'Diversity' },
    { key: 'recency', label: 'Recency' },
    { key: 'consistency', label: 'Consistency' },
];

function resolveSourceOpenTarget(item) {
    const sourceType = String(item?.source_type || item?.source_type_label || '').toLowerCase();
    const title = String(item?.title || item?.label || '');
    const sourceId = item?.file_id || item?.source_id || item?.evidence_reference_id;
    const openLabel = String(item?.open_label || '').toLowerCase();

    if (sourceType === 'gap' || openLabel.includes('evidence & follow')) {
        return { kind: 'module', moduleKey: 'evidence_follow_ups' };
    }
    if (sourceType === 'finding' || openLabel.includes('key findings')) {
        return { kind: 'module', moduleKey: 'key_findings' };
    }
    if (
        sourceType.includes('obs') ||
        sourceType === 'observation' ||
        /^OBS[-_\s]?\d+/i.test(title)
    ) {
        const obsMatch = title.match(/OBS[-_\s]?(\d+)/i);
        const observationId = item?.observation_id || obsMatch?.[1] || sourceId;
        if (observationId && String(observationId).match(/^\d+$/)) {
            return { kind: 'route', route: `/observations?observationId=${observationId}` };
        }
        return { kind: 'route', route: '/observations' };
    }
    if (sourceType.includes('irl') || /^IRL\b/i.test(title) || openLabel.includes('irl')) {
        return { kind: 'route', route: '/irl' };
    }
    if (
        sourceType.includes('vdr') ||
        sourceType === 'document' ||
        sourceType === 'pdf' ||
        /\.(pdf|docx?|pptx?)$/i.test(title)
    ) {
        if (item?.file_id) {
            return { kind: 'route', route: `/dataroom?fileId=${item.file_id}` };
        }
        return { kind: 'route', route: '/dataroom' };
    }
    return { kind: 'route', route: '/dataroom' };
}

function StrengthBar({ label, metric, showPercent = false }) {
    const score = Number(metric?.score ?? 0);
    const pct = Math.max(0, Math.min(100, Math.round(score * 100)));
    return (
        <div className={styles.strengthRow}>
            <span className={styles.strengthLabel}>{label}</span>
            <div className={styles.strengthTrack}>
                <div className={styles.strengthFill} style={{ width: `${pct}%` }} />
            </div>
            <span className={styles.strengthValue}>
                {showPercent ? `${pct}%` : metric?.label || '—'}
            </span>
        </div>
    );
}

const IO_STRENGTH_HEIGHTS = [40, 40, 30, 20, 10];

function coverageFilledCount(coverage) {
    const value = String(coverage || '').toLowerCase();
    if (value.includes('full') || value.includes('strong') || value.includes('complete')) return 5;
    if (value.includes('high')) return 4;
    if (value.includes('limited') || value.includes('low')) return 2;
    if (value.includes('none') || value.includes('insufficient') || value.includes('unavailable')) {
        return 1;
    }
    return 3;
}

function coverageLabel(coverage) {
    const value = String(coverage || 'partial').replace(/_/g, ' ').trim();
    if (!value) return 'Partial coverage';
    return `${value.charAt(0).toUpperCase()}${value.slice(1)} coverage`;
}

function IoStrengthChart({ coverage, metrics }) {
    const scores = (metrics || [])
        .map(metric => Number(metric?.score))
        .filter(score => Number.isFinite(score));
    const filled =
        scores.length >= 3
            ? scores.filter(score => score >= 0.45).length
            : coverageFilledCount(coverage);
    const count = Math.max(1, Math.min(5, filled));
    return (
        <div className={styles.ioStrengthChart}>
            {IO_STRENGTH_HEIGHTS.map((height, index) => (
                <span
                    key={height + index}
                    className={
                        index < count ? styles.ioStrengthBarFilled : styles.ioStrengthBarEmpty
                    }
                    style={{ height }}
                    aria-hidden
                />
            ))}
            <span className={styles.ioStrengthLabel}>{coverageLabel(coverage)}</span>
        </div>
    );
}

function EvidenceLevelBadge({ level }) {
    const normalized = String(level || '').toLowerCase();
    if (normalized === 'strong') {
        return (
            <span className={`${styles.swotEvidenceBadge} ${styles.swotEvidenceStrong}`}>
                <CheckmarkCircle12Filled aria-hidden />
                Strong evidence
            </span>
        );
    }
    if (normalized === 'limited') {
        return (
            <span className={`${styles.swotEvidenceBadge} ${styles.swotEvidenceLimited}`}>
                <Circle12Regular aria-hidden />
                Limited evidence
            </span>
        );
    }
    if (normalized === 'moderate') {
        return (
            <span className={`${styles.swotEvidenceBadge} ${styles.swotEvidenceModerate}`}>
                <Warning12Filled aria-hidden />
                Moderate evidence
            </span>
        );
    }
    return null;
}

function EvidenceItemCard({ item, onOpen }) {
    return (
        <article className={styles.evidenceItem}>
            <div className={styles.evidenceItemHeader}>
                <Document16Regular className={styles.evidenceDocIcon} aria-hidden />
                <div className={styles.evidenceItemText}>
                    <p className={styles.evidenceItemTitle}>{item.title}</p>
                    <p className={styles.evidenceItemMeta}>{item.meta}</p>
                </div>
            </div>
            {item.excerpt ? (
                <blockquote className={styles.evidenceQuote}>“{item.excerpt}”</blockquote>
            ) : null}
            {item.relevance ? (
                <p className={styles.evidenceRelevance}>
                    <span className={styles.evidenceRelevanceLabel}>Relevance</span>
                    {item.relevance}
                </p>
            ) : null}
            <button type="button" className={styles.linkPrimary} onClick={() => onOpen?.(item)}>
                {item.open_label || 'Open source →'}
            </button>
        </article>
    );
}

/**
 * Universal Evidence Panel drawer.
 * Highlights cards use the Highlights evidence endpoint; other modules pass
 * `endpointMode="scope"` / `"object"`. SWOT uses `variant="swot"` (Figma 6635:24673).
 */
export default function HighlightsEvidencePanel({
    open,
    projectId,
    area,
    objectType = null,
    objectId = null,
    onClose,
    onAction,
    onOpenModule,
    endpointMode = 'highlights',
    variant = 'default',
    contextLabel = null,
    evidenceLevel = null,
    itemTitle = null,
    lensConclusion = null,
    reasoningSteps = null,
    evidenceCoverage = null,
}) {
    const navigate = useNavigate();
    const analysisContext = useInsightsAnalysisContext();
    const isSwot = variant === 'swot';
    const isInvestment = variant === 'investment';
    const [tab, setTab] = useState('supporting');
    const [panel, setPanel] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const hasObject = Boolean(objectType && objectId);
        const scopeArea =
            endpointMode === 'scope' && area && !isIntelligenceInsightsScopeArea(area)
                ? null
                : area;
        const hasArea = Boolean(scopeArea);
        if (!open || !projectId || (!hasObject && !hasArea)) {
            if (open && !hasObject && area && !hasArea) {
                setPanel(null);
                setError('Could not load evidence panel for this selection.');
                setLoading(false);
            }
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        setTab('supporting');
        (async () => {
            try {
                let url;
                if (endpointMode === 'object' || hasObject) {
                    url = INTELLIGENCE_INSIGHTS_V2_OBJECT_EVIDENCE(
                        projectId,
                        objectType,
                        objectId,
                    );
                } else if (endpointMode === 'scope') {
                    url = INTELLIGENCE_INSIGHTS_V2_SCOPE_EVIDENCE(projectId, scopeArea);
                } else {
                    url = INTELLIGENCE_INSIGHTS_V2_HIGHLIGHTS_EVIDENCE(projectId, scopeArea);
                }
                url = withInsightsContextQuery(url, analysisContext);
                const { data } = await axiosInstance.get(url);
                if (!cancelled) setPanel(data || null);
            } catch (err) {
                const detail = err?.response?.data?.detail;
                if (!cancelled) {
                    setPanel(null);
                    setError(typeof detail === 'string' ? detail : 'Could not load evidence panel.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, projectId, area, endpointMode, objectType, objectId, analysisContext]);

    const selected = panel?.selected_object || {};
    const rag = String(selected.rag || 'grey').toLowerCase();
    const ragClass = RAG_CLASS[rag] || styles.ragGrey;
    const tabs = panel?.tabs || {};
    const activeTab = TABS.find(t => t.key === tab) || TABS[0];
    const items = tabs[activeTab.apiKey] || [];
    const strength = panel?.evidence_strength || {};
    const related = panel?.related_intelligence || [];
    const actions = panel?.actions || [];
    const summary = panel?.evidence_summary || {};

    const handleOpenSource = item => {
        const target = resolveSourceOpenTarget(item);
        onClose?.();
        if (target.kind === 'module') {
            if (onOpenModule) {
                onOpenModule(target.moduleKey);
                return;
            }
            if (onAction) {
                onAction(
                    {
                        key: `open_${target.moduleKey}`,
                        label: item.open_label || 'Open',
                        actionType: `open_${target.moduleKey}`,
                    },
                    { area },
                );
                return;
            }
        }
        if (target.kind === 'route' && target.route) {
            navigate(target.route);
            return;
        }
        toast.info('This source link is not available yet.');
    };

    const handleAction = action => {
        if (!action) return;
        if (onAction) {
            onAction(action, {
                area,
                displayLabel: selected.label,
                rag: selected.rag,
                headline: itemTitle || panel?.lens_conclusion,
                conclusion: panel?.lens_conclusion,
                whyItMatters: panel?.reasoning_steps?.map(s => s.statement).join(' '),
                relatedRiskIds: (panel?.related_intelligence || [])
                    .filter(r => r.kind === 'risk')
                    .map(r => r.id),
                evidenceCoverage: null,
            });
            return;
        }
        toast.info(`${action.label} will open from the shared action workflow.`);
    };

    const handleRelated = item => {
        const dest = item?.destination_key;
        if (!dest) return;
        onClose?.();
        if (onOpenModule) {
            onOpenModule(dest);
            return;
        }
        toast.info(`${item.destination_label || 'Related module'} is not available yet.`);
    };

    const footerActions = filterSupportedInsightsActions(
        actions.length > 0
            ? actions
            : isInvestment
              ? [
                    { key: 'save_to_journal', label: 'Save to Journal', placement: 'secondary' },
                    { key: 'add_to_raid', label: 'Add to RAID', placement: 'secondary' },
                ]
              : isSwot
                ? [{ key: 'save_to_journal', label: 'Save to Journal', placement: 'secondary' }]
                : [],
        {
            moduleKey: isInvestment
                ? 'investment_opinion'
                : isSwot
                  ? 'swot_analysis'
                  : 'highlights',
        },
    );
    const conclusionText = lensConclusion || panel?.lens_conclusion;
    const reasoning = reasoningSteps?.length ? reasoningSteps : panel?.reasoning_steps || [];

    const strengthBars = isSwot ? SWOT_STRENGTH_BARS : HIGHLIGHT_STRENGTH_BARS;
    const hasStrength = strengthBars.some(
        bar => strength[bar.key] || (bar.fallback && strength[bar.fallback]),
    );

    const drawerWidth = isSwot || isInvestment ? 480 : 440;

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={typeof document !== 'undefined' ? document.body : undefined}
            onOpenChange={(_, data) => {
                if (!data.open) onClose?.();
            }}
            style={{ width: drawerWidth, maxWidth: '100vw' }}>
            <DrawerBody
                className={`${styles.evidenceDrawerBody} ${
                    isSwot ? styles.evidenceDrawerBodySwot : ''
                }`}>
                <div className={styles.evidenceRoot}>
                    {isInvestment ? (
                        <div className={styles.evidenceHeader}>
                            <div className={styles.evidenceHeaderMain}>
                                <h2 className={styles.evidenceTitle}>Evidence</h2>
                            </div>
                            <button
                                type="button"
                                className={styles.drawerClose}
                                onClick={onClose}
                                aria-label="Close">
                                <Dismiss12Regular />
                            </button>
                        </div>
                    ) : isSwot ? (
                        <div className={styles.swotEvidenceHeader}>
                            <div className={styles.swotEvidenceHeaderMain}>
                                <div className={styles.swotEvidenceMetaRow}>
                                    {contextLabel ? (
                                        <span className={styles.swotContextLabel}>
                                            {contextLabel}
                                        </span>
                                    ) : null}
                                    <EvidenceLevelBadge level={evidenceLevel} />
                                </div>
                                <h2 className={styles.swotEvidenceTitle}>
                                    {itemTitle || selected.label || 'Evidence'}
                                </h2>
                            </div>
                            <button
                                type="button"
                                className={styles.drawerClose}
                                onClick={onClose}
                                aria-label="Close">
                                <Dismiss12Regular />
                            </button>
                        </div>
                    ) : (
                        <div className={styles.evidenceHeader}>
                            <div className={styles.evidenceHeaderMain}>
                                <div className={styles.evidenceTitleRow}>
                                    <h2 className={styles.evidenceTitle}>Evidence</h2>
                                    {selected.label ? (
                                        <span className={styles.evidenceAreaChip}>
                                            {selected.label}
                                        </span>
                                    ) : null}
                                    {selected.rag ? (
                                        <span className={`${styles.ragPill} ${ragClass}`}>
                                            <span className={styles.ragPillDot} aria-hidden />
                                            {String(selected.rag).replace(/^\w/, c =>
                                                c.toUpperCase(),
                                            )}
                                        </span>
                                    ) : null}
                                </div>
                                <p className={styles.evidenceSubtitle}>
                                    Scope: {selected.label || '—'} · Type:{' '}
                                    {selected.object_type_label || 'Highlight'} · Materiality:{' '}
                                    {selected.materiality
                                        ? String(selected.materiality).replace(/^\w/, c =>
                                              c.toUpperCase(),
                                          )
                                        : 'Material'}
                                </p>
                            </div>
                            <button
                                type="button"
                                className={styles.drawerClose}
                                onClick={onClose}
                                aria-label="Close">
                                <Dismiss12Regular />
                            </button>
                        </div>
                    )}

                    {!isSwot && !isInvestment ? (
                        <div className={styles.evidenceTabs}>
                            {TABS.map(item => (
                                <button
                                    key={item.key}
                                    type="button"
                                    className={`${styles.evidenceTab} ${
                                        tab === item.key ? styles.evidenceTabActive : ''
                                    }`}
                                    onClick={() => setTab(item.key)}>
                                    {item.label}
                                    {summary?.[item.apiKey] != null &&
                                    typeof summary[item.apiKey] !== 'object' ? (
                                        <span className={styles.evidenceTabCount}>
                                            {summary[item.apiKey]}
                                        </span>
                                    ) : null}
                                </button>
                            ))}
                        </div>
                    ) : null}

                    <div className={styles.evidenceBody}>
                        {loading ? <p className={styles.evidenceEmpty}>Loading evidence…</p> : null}
                        {error ? (
                            <p className={styles.evidenceEmpty}>
                                <Warning12Filled aria-hidden /> {error}
                            </p>
                        ) : null}

                        {!loading && !error && panel ? (
                            <>
                                <section
                                    className={
                                        isSwot || isInvestment
                                            ? styles.swotConclusionBox
                                            : styles.evidenceSection
                                    }>
                                    <p className={styles.drawerSectionTitle}>Lens Conclusion</p>
                                    <p
                                        className={
                                            isSwot || isInvestment
                                                ? styles.swotConclusionText
                                                : styles.lensConclusion
                                        }>
                                        {conclusionText}
                                    </p>
                                </section>

                                {reasoning.length ? (
                                    <section className={styles.evidenceSection}>
                                        <p className={styles.drawerSectionTitle}>Reasoning</p>
                                        <ol
                                            className={
                                                isInvestment
                                                    ? styles.ioReasoningList
                                                    : isSwot
                                                      ? styles.swotReasoningList
                                                      : styles.reasoningList
                                            }>
                                            {reasoning.map(step => (
                                                <li
                                                    key={step.step}
                                                    className={
                                                        isInvestment
                                                            ? styles.ioReasoningItem
                                                            : isSwot
                                                              ? styles.swotReasoningItem
                                                              : styles.reasoningItem
                                                    }>
                                                    {isInvestment ? (
                                                        <span className={styles.ioReasoningIndex}>
                                                            {step.step}
                                                        </span>
                                                    ) : isSwot ? (
                                                        <span className={styles.swotReasoningIndex}>
                                                            {step.step}.
                                                        </span>
                                                    ) : null}
                                                    <span className={styles.reasoningText}>
                                                        {step.statement}
                                                    </span>
                                                    {!isSwot &&
                                                    !isInvestment &&
                                                    step.evidence_reference_ids?.length ? (
                                                        <span className={styles.citationChips}>
                                                            {step.evidence_reference_ids.map(id => (
                                                                <span
                                                                    key={id}
                                                                    className={styles.citationChip}>
                                                                    [{id}]
                                                                </span>
                                                            ))}
                                                        </span>
                                                    ) : null}
                                                </li>
                                            ))}
                                        </ol>
                                        {!isSwot && typeof summary?.display === 'string' && summary.display ? (
                                            <p className={styles.evidenceSummaryLine}>
                                                {summary.display}
                                            </p>
                                        ) : null}
                                    </section>
                                ) : null}

                                {isSwot ? (
                                    <section className={styles.evidenceSection}>
                                        <p className={styles.drawerSectionTitle}>
                                            Evidence summary
                                        </p>
                                        <p className={styles.swotSummaryLine}>
                                            {typeof summary.display === 'string'
                                                ? summary.display
                                                : `${
                                                    (summary.supporting || 0) +
                                                    (summary.contradictory || 0) +
                                                    (summary.claims_and_inferences || 0) +
                                                    (summary.gaps || 0)
                                                } items · Supporting: ${
                                                    summary.supporting || 0
                                                } · Contradictory: ${
                                                    summary.contradictory || 0
                                                } · Gaps: ${summary.gaps || 0}`}
                                        </p>
                                    </section>
                                ) : null}

                                {isInvestment ? (
                                    <div className={styles.ioEvidencePills}>
                                        {TABS.map(item => (
                                            <button
                                                key={item.key}
                                                type="button"
                                                className={`${styles.ioEvidencePill} ${
                                                    tab === item.key
                                                        ? styles.ioEvidencePillActive
                                                        : ''
                                                }`}
                                                onClick={() => setTab(item.key)}>
                                                {item.key === 'claims' ? 'Claims' : item.label}
                                            </button>
                                        ))}
                                    </div>
                                ) : isSwot ? (
                                    <div className={styles.swotEvidenceTabs}>
                                        {TABS.map(item => (
                                            <button
                                                key={item.key}
                                                type="button"
                                                className={`${styles.swotEvidenceTab} ${
                                                    tab === item.key
                                                        ? styles.swotEvidenceTabActive
                                                        : ''
                                                }`}
                                                onClick={() => setTab(item.key)}>
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                ) : null}

                                <section className={styles.evidenceSection}>
                                    {isInvestment ? (
                                        <p className={styles.drawerSectionTitle}>
                                            Evidence summary
                                        </p>
                                    ) : !isSwot ? (
                                        <p className={styles.drawerSectionTitle}>
                                            {activeTab.label}
                                        </p>
                                    ) : (
                                        <p className={styles.swotTabHint}>
                                            {activeTab.key === 'supporting'
                                                ? 'Supporting evidence sources referenced in this assessment.'
                                                : activeTab.key === 'contradictory'
                                                  ? 'Sources that conflict with or qualify this assessment.'
                                                  : activeTab.key === 'claims'
                                                    ? 'Management claims and Lens inferences tied to this item.'
                                                    : 'Evidence gaps that limit confidence in this assessment.'}
                                        </p>
                                    )}
                                    {items.length ? (
                                        isInvestment ? (
                                            items.map(item => (
                                                <button
                                                    key={`${item.evidence_reference_id}-${item.title}`}
                                                    type="button"
                                                    className={styles.ioEvidenceBlock}
                                                    onClick={() => handleOpenSource(item)}>
                                                    {item.excerpt &&
                                                    item.excerpt !== item.title
                                                        ? item.excerpt
                                                        : item.title || item.excerpt || item.meta}
                                                </button>
                                            ))
                                        ) : (
                                            items.map(item => (
                                                <EvidenceItemCard
                                                    key={`${item.evidence_reference_id}-${item.title}`}
                                                    item={item}
                                                    onOpen={handleOpenSource}
                                                />
                                            ))
                                        )
                                    ) : (
                                        <p className={styles.evidenceEmpty}>
                                            No {activeTab.label.toLowerCase()} items for this
                                            {isSwot || isInvestment ? ' item' : ' highlight'}.
                                        </p>
                                    )}
                                </section>

                                {isInvestment ? (
                                    <section className={styles.evidenceSection}>
                                        <p className={styles.drawerSectionTitle}>
                                            Evidence strength
                                        </p>
                                        <IoStrengthChart
                                            coverage={
                                                evidenceCoverage ||
                                                strength.coverage ||
                                                selected.evidence_coverage ||
                                                panel.evidence_coverage ||
                                                summary.coverage
                                            }
                                            metrics={strengthBars.map(
                                                bar =>
                                                    strength[bar.key] ||
                                                    (bar.fallback ? strength[bar.fallback] : null),
                                            )}
                                        />
                                    </section>
                                ) : hasStrength ? (
                                    <section className={styles.evidenceSection}>
                                        <p className={styles.drawerSectionTitle}>
                                            Evidence strength
                                        </p>
                                        {strengthBars.map(bar => {
                                            const metric =
                                                strength[bar.key] ||
                                                (bar.fallback ? strength[bar.fallback] : null);
                                            if (!metric) return null;
                                            return (
                                                <StrengthBar
                                                    key={bar.key}
                                                    label={bar.label}
                                                    metric={metric}
                                                    showPercent={isSwot}
                                                />
                                            );
                                        })}
                                    </section>
                                ) : null}

                                {related.length ? (
                                    <section className={styles.evidenceSection}>
                                        <p className={styles.drawerSectionTitle}>
                                            Related intelligence
                                        </p>
                                        {isSwot || isInvestment ? (
                                            <ul className={styles.swotRelatedList}>
                                                {related.map(item => (
                                                    <li key={`${item.kind}-${item.id}`}>
                                                        <button
                                                            type="button"
                                                            className={styles.swotRelatedLink}
                                                            onClick={() => handleRelated(item)}>
                                                            →{' '}
                                                            {item.destination_label ||
                                                                item.label ||
                                                                'Open related'}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <ul className={styles.relatedList}>
                                                {related.map(item => (
                                                    <li
                                                        key={`${item.kind}-${item.id}`}
                                                        className={styles.relatedItem}>
                                                        <span className={styles.relatedLabel}>
                                                            {item.label}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className={styles.relatedDest}
                                                            onClick={() => handleRelated(item)}>
                                                            {item.destination_label}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </section>
                                ) : null}
                            </>
                        ) : null}
                    </div>

                    {footerActions.length ? (
                        <div className={styles.drawerFooter}>
                            {footerActions.map(action => (
                                <button
                                    key={action.key}
                                    type="button"
                                    className={
                                        !isSwot && action.placement === 'primary'
                                            ? styles.btnPrimary
                                            : styles.btnOutline
                                    }
                                    onClick={() => handleAction(action)}>
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}
